import React from 'react';
import { produce } from 'immer';
import { eventNames, type EventName, EmailTypes } from '../../lib/constants';
import { concatStrings, bold, concatObject, boldAll } from '../../lib/utils';
import type { IAuditHistoryBody, IEventData, IRecipientBody } from '../../store/envelopeHistory/types';

export type RecipientInfoMap = { [id: string]: IRecipientBody };
export type AuditEventWithRecipientInfo = IAuditHistoryBody & { recipientInfo: RecipientInfoMap };

export function getRecipientActionType(event: IAuditHistoryBody) {
  if ([eventNames.SignerAdded, eventNames.CarbonCopyAdded].includes(event.eventName)) {
    return 'add';
  }
  if ([eventNames.SignerUpdated, eventNames.CarbonCopyUpdated].includes(event.eventName)) {
    return 'update';
  }
  if ([eventNames.SignerDeleted, eventNames.CarbonCopyDeleted].includes(event.eventName)) {
    return 'delete';
  }
  return null;
}

const emailNames = {
  [EmailTypes.ReviewAndSign]: 'Resent envelope to ',
  [EmailTypes.Completed]: 'Envelope Completed email sent to ',
  [EmailTypes.Canceled]: 'Envelope Canceled email sent to ',
};

export function getEventsWithRecipientInfo(events: IAuditHistoryBody[], recipientInfoMap: RecipientInfoMap) {
  let currentStateOfRecipients = { ...recipientInfoMap };

  return events
    .filter(
      (e) =>
        ![
          eventNames.SignerDeleted,
          eventNames.CarbonCopyDeleted,
          eventNames.SignerAdded,
          eventNames.CarbonCopyAdded,
        ].includes(e.eventName)
    )
    .map((event) => {
      const actionType = getRecipientActionType(event);
      const eventData = event.eventData as IEventData;

      currentStateOfRecipients = produce(currentStateOfRecipients, (draft) => {
        if (actionType === 'update' && eventData.recipientId) {
          draft[eventData.recipientId] = {
            ...draft[eventData.recipientId],
            firstName: eventData.originalFirstName,
            lastName: eventData.originalLastName,
            email: eventData.originalEmail,
          };
        }
      });

      return { ...event, recipientInfo: currentStateOfRecipients };
    });
}

export function generateEventContent(args: {
  event: AuditEventWithRecipientInfo;
  senderId: string | undefined;
  senderName: string;
  recipientsWhoHaveNotViewed?: IRecipientBody[];
}) {
  const { event, senderId, senderName, recipientsWhoHaveNotViewed } = args;

  const eventData = event.eventData as IEventData;

  const originalRecipientName = concatStrings(
    eventData.originalFirstName,
    eventData.originalLastName,
    `(${eventData.originalEmail})`
  );
  const updatedRecipientName = concatStrings(
    eventData.updatedFirstName,
    eventData.updatedLastName,
    `(${eventData.updatedEmail!})`
  );

  const isDelegate = event.userType === 'sender' && event.userId !== senderId;
  const canceledBySystem = event.userType === 'system';

  let userFullName = event.userName?.trim() || senderName;
  if (isDelegate) {
    userFullName += ` on behalf of ${senderName}`;
  }
  const allRecipients = Object.values(event.recipientInfo);

  const recipientName = concatObject(
    allRecipients.find((r) => (r.id === event.userId && !eventData.recipientId) || r.id === eventData.recipientId)
  );

  const recipientWhoHasNotViewedName = concatObject(
    recipientsWhoHaveNotViewed?.find(
      (r) => (r.id === event.userId && !eventData.recipientId) || r.id === eventData.recipientId
    )
  );

  const oldEnvelopeName = event.eventName === eventNames.EnvelopeNameUpdated ? eventData.oldEnvelopeName : '';

  const tableCellContent: Record<EventName, React.ReactNode> = {
    EnvelopeCreated: <>Created by {bold(userFullName)}</>,
    EnvelopeCorrecting: <>Envelope being corrected by {bold(userFullName)}</>,
    EnvelopeViewed: <>Viewed by {bold(recipientName)}</>,
    EnvelopeSigned: event.signedInPerson ? (
      <>Signed in Person by {bold(recipientName)}</>
    ) : (
      <>Signed by {bold(recipientName)}</>
    ),
    EnvelopeCanceled: canceledBySystem ? 'Envelope canceled by system' : <>Envelope Canceled by {bold(userFullName)}</>,
    EnvelopeCompleted: 'Envelope Completed',
    SignatureAccepted: <>{bold(recipientName)} has adopted their electronic signature</>,
    WaitingFor: <>Waiting for {bold(recipientWhoHasNotViewedName)} to view documents</>,
    EnvelopeDownloaded: (
      <>
        Envelope Downloaded by{' '}
        {allRecipients.find((r) => r.id === event.userId) ? bold(recipientName) : bold(userFullName)}
      </>
    ),
    SignerUpdated: (
      <>
        Details for {bold(originalRecipientName)} updated to {bold(updatedRecipientName)}
      </>
    ),
    CarbonCopyUpdated: (
      <>
        Details for {bold(originalRecipientName)} updated to {bold(updatedRecipientName)}
      </>
    ),
    ESignDisclosureAccepted: <>{bold(recipientName)} has agreed to the ESIGN Consumer Disclosure and Consent</>,
    EnvelopeSent: (
      <>
        Sent to {boldAll(allRecipients)} by {bold(userFullName)}
      </>
    ),
    NotificationRequested: (
      <>
        {emailNames[eventData.emailType as keyof typeof emailNames]}
        {allRecipients.find((r) => eventData.recipientId != null && r.id === eventData.recipientId)
          ? bold(recipientName)
          : bold(userFullName)}
      </>
    ),
    EnvelopeNameUpdated: oldEnvelopeName ? (
      <>
        Envelope name was updated from {bold(oldEnvelopeName)} to {bold(eventData.newEnvelopeName)} by{' '}
        {bold(userFullName)}
      </>
    ) : (
      <>
        Envelope name was updated to {bold(eventData.newEnvelopeName)} by {bold(userFullName)}
      </>
    ),
    SignerAdded: null,
    SignerDeleted: null,
    CarbonCopyAdded: null,
    CarbonCopyDeleted: null,
  };

  return tableCellContent[event.eventName];
}
